import { useEffect } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { notification } from 'antd';

import { useAuthorization } from '@/shared/hooks/useAuthorization';
import { getRolePermissions } from '@/shared/lib/permissions/rolePermissions';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { useAuthStore } from '@/entities/auth';
import type { Role } from '@/entities/auth';

const IS_DEMO_MODE = import.meta.env.MODE === 'demo';

type ProtectedRouteProps = {
  children?: React.ReactNode;
  requiredRoles?: Role[];
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, roles } = useAuthStore();
  const { hasAnyRole } = useAuthorization();
  const location = useLocation();

  const hasPermission = (() => {
    const customPermissions = getRolePermissions();

    if (customPermissions && roles && roles.length > 0) {
      const userRoles = roles.filter((role): role is Role => Boolean(role) && typeof role === 'string');
      const pathname = location.pathname;

      const hasRoutePermission = userRoles.some((role) => {
        const allowedRoutes = customPermissions[role] || [];
        return allowedRoutes.some((route) => pathname === route || pathname.startsWith(route + '/'));
      });

      return hasRoutePermission;
    }

    return requiredRoles.length === 0 || hasAnyRole(requiredRoles);
  })();

  useEffect(() => {
    if (isAuthenticated && !hasPermission) {
      notification.warning({
        message: '접근 권한 없음',
        description: '해당 페이지에 접근할 권한이 없습니다.',
        placement: 'bottomLeft',
        duration: 3,
      });
    }
  }, [isAuthenticated, hasPermission, location.pathname]);

  if (!isAuthenticated) {
    if (IS_DEMO_MODE) {
      return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
    }

    return <LoadingState message="인증 확인 중..." />;
  }

  if (!hasPermission) {
    return <Navigate to="/" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};
